import lodash from 'lodash'
import Vue from 'vue'

import * as hotbApi from '@/api/hotb/request'
import { timeOptions } from '@/modules/dashboards/consts'
import types from '@/store/mutation-types'
import { forceUpdateVueObject } from '@/store/util'
import { setPhysicalSite } from '@/utils/siteUtils'

const defaultState = getDefaultState()

function getDefaultState () {
  return {
    currentHotb: {},
    hkldHandoverContacts: [{
      name: '',
      phone: '',
      email: '',
    }],
    lastDashboardDateFilterIndex: timeOptions.length - 1,
    myTenantProjectsFilterDateIndex: timeOptions.length - 1,
    lastSearchedAttributes: {},
    allHotbs: [],
    viewAllHotbs: [],
  }
}

const getters = {
  currentHotb: state => state.currentHotb,
  hkldHandoverContacts: state => state.hkldHandoverContacts,
  lastDashboardDateFilterIndex: state => state.lastDashboardDateFilterIndex,
  myTenantProjectsFilterDateIndex: state => state.myTenantProjectsFilterDateIndex,
  lastSearchedAttributes: state => state.lastSearchedAttributes,
  allHotbs: state => state.allHotbs,
  viewAllHotbs: state => state.viewAllHotbs,
  isHandover: state => state.currentHotb.type === 'HANDOVER',
}

const actions = {
  async setHotbPhysicalSite ({ commit, state }, hotb) {
    const building = lodash.get(hotb, 'tenancy.building', {})
    let result = hotb
    if (building.id) {
      result = await setPhysicalSite(building.id, hotb)
    }
    return result
  },
  async createHotb ({ commit }, { tenancy, type }) {
    const hotb = await hotbApi.createHotb(tenancy, type)
    commit(types.HOTB_SET_CURRENT, { hotb })
    return hotb.id
  },

  async resetQueryAllIfNotMatch ({ commit, state }, tenancyId) {
    let firstTenancyId = deep(state, ['allHotbs', 0, 'tenancy', 'id'])
    if (firstTenancyId && firstTenancyId !== tenancyId) {
      commit(types.HOTB_LOAD_ALL, { hotbs: [] })
      commit(types.SET_LAST_SEARCH_ATTRIBUTES, { lastSearchedAttributes: {} })
    }
  },

  async loadHotb ({ commit, dispatch }, id) {
    if (!id) return
    const hotb = await hotbApi.getHotb(id)
    const result = await dispatch('setHotbPhysicalSite', hotb)
    commit(types.HOTB_SET_CURRENT, { hotb: result })
  },

  setHotbType ({ commit }, type) {
    if (!type) return
    commit(types.HOTB_SET_CURRENT_TYPE, { type })
  },

  clearHotb ({ commit }) {
    const hotb = { type: '' }
    commit(types.HOTB_SET_CURRENT, { hotb })
  },

  async updateHotb ({ state }, { attrs, notify, action } = { notify: true }) {
    let request = {}
    if (Array.isArray(attrs) && attrs.length > 0) {
      attrs.forEach(attr => {
        request[attr] = state.currentHotb[attr]
      })
    } else {
      request = state.currentHotb
    }
    const ret = await hotbApi.updateHotb(state.currentHotb.id, request, notify, action)
    return ret
  },

  async patchStatus ({ state }, { request, action }) {
    await hotbApi.patchStatus(state.currentHotb.id, request, action)
  },

  setLastDashboardDateFilterIndex ({ commit }, { index }) {
    commit(types.HOTB_SET_FILTER_DATE_INDEX, { index })
  },
  setMyTenantProjectsFilterDateIndex ({ commit }, { index }) {
    commit(types.HOTB_SET_MY_TENANT_PROJECTS_FILTER_DATE_INDEX, { index })
  },
  async loadAllHotbs ({ commit, state, dispatch }, query) {
    const allHotbs = await hotbApi.listAll(query)
    commit(types.HOTB_LOAD_ALL, { hotbs: allHotbs })
  },
  async loadViewAllHotbs ({ commit, state, dispatch }, { query, diskCacheKey }) {
    const allHotbs = await hotbApi.listAll(query, diskCacheKey)
    commit(types.HOTB_LOAD_VIEW_ALL, { hotbs: allHotbs })
  },
  setLastSearchedAttributes ({ commit }, lastSearchedAttributes) {
    const attributes = lodash.cloneDeep(lastSearchedAttributes)
    commit(types.SET_LAST_SEARCH_ATTRIBUTES, { lastSearchedAttributes: attributes })
  },
}

const mutations = {
  [types.HOTB_SET_CURRENT] (state, { hotb }) {
    state.currentHotb = hotb
  },

  [types.HOTB_SET_CURRENT_TYPE] (state, { type }) {
    state.currentHotb.type = type
  },

  [types.HOTB_SET_FILTER_DATE_INDEX] (state, { index }) {
    state.lastDashboardDateFilterIndex = index
  },
  [types.HOTB_SET_MY_TENANT_PROJECTS_FILTER_DATE_INDEX] (state, { index }) {
    state.myTenantProjectsFilterDateIndex = index
  },

  [types.HOTB_UPDATE_FIELD_PATH] (state, { path, newValue }) {
    forceUpdateVueObject(state.currentHotb, path, newValue)
  },
  [types.HOTB_UPDATE_HKLD_HANDOVER_CONTACTS] (state, payload) {
    forceUpdateVueObject(state, ['hkldHandoverContacts'], payload)
  },
  [types.HOTB_SCHEDULE_DELETE] (state, indexToRemove) {
    const schedules = state.currentHotb.schedules || []
    schedules.splice(indexToRemove, 1)
  },
  [types.HOTB_LOAD_ALL] (state, { hotbs }) {
    state.allHotbs = hotbs
  },
  [types.HOTB_LOAD_VIEW_ALL] (state, { hotbs }) {
    state.viewAllHotbs = hotbs
  },
  [types.SET_LAST_SEARCH_ATTRIBUTES] (state, {lastSearchedAttributes}) {
    state.lastSearchedAttributes = lastSearchedAttributes
  },
  [types.RESET_STATE] (state) {
    const stateVal = getDefaultState()
    for (const field in stateVal) {
      Vue.set(state, field, stateVal[field])
    }
  },
}

export default {
  namespaced: true,
  state: defaultState,
  getters,
  actions,
  mutations,
}
